import { useCallback, useEffect, useMemo, useState } from "react";

import { AnimatePresence } from "../AuthNew/shared/motionUtils";
import {
  Modal,
  ModalContent,
  ModalContentWrapper,
  ModalOverlay,
  StepContent,
  fadeInOut,
} from "./shared/onboardingStyles";
import Step1Invitation from "./steps/Step1Invitation";
import Step1JoinRequest from "./steps/Step1JoinRequest";
import Step1Organization from "./steps/Step1Organization";
import Step2OrganizationType from "./steps/Step2OrganizationType";
import Step3InviteTeam from "./steps/Step3InviteTeam";
import Step3TeamSetup from "./steps/Step3TeamSetup";
import Step4GetHelp from "./steps/Step4GetHelp";
import Step5Loading from "./steps/Step5Loading";

const planIds = [
  {
    name: "standard-Monthly",
    id: "390ce89f-9bdb-45a0-a9bc-19298bf40fa3",
  },
  {
    name: "elite-Monthly",
    id: "11eadb08-cb8f-49bc-b668-cc0406b8c77d",
  },
];

const OnboardingFlow = ({
  isOpen,
  pendingInvitation,
  pendingJoinRequest,
  onComplete,
  onSubmit,
  userInfo,
  loginProfileCreatedReset,
  subscriptionPlans,
  selectedOrganization,
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isJoiningOrganization, setIsJoiningOrganization] = useState(null); // null = no choice yet
  const [fromInvitation, setFromInvitation] = useState(false);
  // Removed height animation complexity
  const [formData, setFormData] = useState({
    organizationName: "",
    organizationType: "",
    question1Answer: "",
    question2Answer: "",
    teamSize: "",
    workTypes: [],
    customIndustry: "",
    inviteEmails: [],
    skipInvite: false,
    phoneNumber: "",
  });

  // Determine flow type based on pending join request, pending invitation and user choice
  const hasPendingJoinRequest =
    pendingJoinRequest && pendingJoinRequest !== null;
  const hasPendingInvitation = pendingInvitation && pendingInvitation !== null;
  const hasUserMadeChoice = isJoiningOrganization !== null;
  const isCreatingOrganization =
    hasUserMadeChoice && isJoiningOrganization === false;
  const totalSteps = hasPendingInvitation && !isCreatingOrganization ? 2 : 6; // 2 steps for joining, 6 for creating
  const progress = (currentStep / totalSteps) * 100;

  // // Auto-select Power yearly plan
  // const powerYearlyPlan = useMemo(() => {
  //   if (subscriptionPlans?.length > 0) {
  //     const plan = subscriptionPlans.find(
  //       item => item.name === "Power" && item.interval === "yearly",
  //     );
  //     return plan ? plan.id : null;
  //   }
  //   return null;
  // }, [subscriptionPlans]);

  const handleDataChange = useCallback(data => {
    setFormData(prev => ({ ...prev, ...data }));
  }, []);

  const handleNext = useCallback(() => {
    if (currentStep < totalSteps) {
      setCurrentStep(prev => prev + 1);
    }
  }, [currentStep, totalSteps]);

  const handleJoinOrganization = useCallback(() => {
    setIsJoiningOrganization(true);
    // Skip to loading step for joining organization
    setCurrentStep(2);
  }, []);

  const handleCreateOrganization = useCallback(() => {
    setIsJoiningOrganization(false);
    setFromInvitation(true); // Mark that user came from invitation screen
    // User chose to create organization - they'll stay on step 1
    // The flow will automatically switch to 5-step flow on next render
  }, []);

  const handleBack = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  const handleBackToInvitation = useCallback(() => {
    // Reset the choice and go back to invitation screen
    setIsJoiningOrganization(null);
    setFromInvitation(false);
    setCurrentStep(1);
  }, []);

  const handleLoadingComplete = useCallback(async () => {
    // Build organization object - only include non-empty/non-skipped fields
    const organization = {
      name: formData.organizationName || null,
      type: formData.organizationType || null,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
      plan_id:
        formData.organizationType === "service" ? planIds[1].id : planIds[0].id,
    };

    // Step 1: Organization Setup (already added above)

    // Step 2: Organization Type (already added above)

    // Step 3: Team Setup
    if (formData.teamSize) {
      organization.team_size = formData.teamSize;
    }
    // Industry: send as array - use customIndustry if provided, otherwise use single workType
    const industry =
      formData.customIndustry ||
      (formData.workTypes && formData.workTypes.length > 0
        ? formData.workTypes[0] // Use first (and only) selected work type
        : null);
    if (industry) {
      organization.industry = industry; 
    }

    // Step 5: Phone Number (only include if provided)
    if (formData.phoneNumber) {
      organization.phone = formData.phoneNumber;
    }

    // Transform formData to match backend serializer
    const payload = {
      organization,
    };

    // Step 4: Invite Team - separate top-level invitations array
    // Transform array of email strings to array of objects with email property
    if (
      !formData.skipInvite &&
      formData.inviteEmails &&
      formData.inviteEmails.length > 0
    ) {
      payload.invitations = formData.inviteEmails.map(email => ({
        email: email.trim(),
      }));
    }

    // Submit all data to backend
    // Note: completeOnboarding is a Redux action, errors are handled in reducer
    if (onSubmit) {
      onSubmit(payload);
    }

    // Call completion callback
    if (onComplete) {
      onComplete(formData);
    }

    // Don't show post-onboarding modal here - wait for Redux state to update
    // The useEffect below will handle showing it when onboarding completes successfully
  }, [formData, onSubmit, onComplete]);

  // Removed complex height measurement

  // Prevent scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  // Prevent closing modal with Escape key
  useEffect(() => {
    const handleEscape = e => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    if (isOpen) {
      window.addEventListener("keydown", handleEscape, true);
    }

    return () => {
      window.removeEventListener("keydown", handleEscape, true);
    };
  }, [isOpen]);

  const renderStep = () => {
    // If user has pending join request and hasn't chosen to create organization
    if (
      hasPendingJoinRequest &&
      !(hasUserMadeChoice && isJoiningOrganization === false)
    ) {
      return (
        <Step1JoinRequest
          key="step1-join-request"
          pendingJoinRequest={pendingJoinRequest}
          userInfo={userInfo}
          loginProfileCreatedReset={loginProfileCreatedReset}
          onCreateOrganization={handleCreateOrganization}
        />
      );
    }

    // If user has pending invitation and hasn't made a choice yet, or chose to join, show joining flow
    if (
      hasPendingInvitation &&
      (!hasUserMadeChoice || isJoiningOrganization === true)
    ) {
      // Joining organization flow - only 2 steps
      switch (currentStep) {
        case 1:
          return (
            <Step1Invitation
              key="step1-invitation"
              pendingInvitation={pendingInvitation}
              userInfo={userInfo}
              onJoinOrganization={handleJoinOrganization}
              onCreateOrganization={handleCreateOrganization}
              loginProfileCreatedReset={loginProfileCreatedReset}
            />
          );
        case 2:
          return (
            <Step5Loading
              key="step5-loading-join"
              onComplete={handleLoadingComplete}
              userInfo={userInfo}
              selectedOrganization={selectedOrganization}
            />
          );
        default:
          return null;
      }
    } else {
      // Creating organization flow - all 6 steps (either no invitation or user chose to create)
      switch (currentStep) {
        case 1:
          return (
            <Step1Organization
              key="step1-organization"
              data={formData}
              onChange={handleDataChange}
              onNext={handleNext}
              onBack={fromInvitation ? handleBackToInvitation : handleBack}
              userInfo={userInfo}
              fromInvitation={fromInvitation}
              selectedOrganization={selectedOrganization}
              pendingInvitation={pendingInvitation}
              progress={progress}
            />
          );
        case 2:
          return (
            <Step2OrganizationType
              key="step2-organization-type"
              data={formData}
              onChange={handleDataChange}
              onNext={handleNext}
              onBack={handleBack}
              progress={progress}
            />
          );
        case 3:
          return (
            <Step3TeamSetup
              key="step3-team"
              data={formData}
              onChange={handleDataChange}
              onNext={handleNext}
              onBack={handleBack}
              progress={progress}
            />
          );
        case 4:
          return (
            <Step3InviteTeam
              key="step4-invite-team"
              data={formData}
              onChange={handleDataChange}
              onNext={handleNext}
              onBack={handleBack}
              userInfo={userInfo}
              selectedOrganization={selectedOrganization}
              progress={progress}
            />
          );
        case 5:
          return (
            <Step4GetHelp
              key="step5-phone"
              data={formData}
              onChange={handleDataChange}
              onNext={handleNext}
              onBack={handleBack}
              isJoiningOrganization={isJoiningOrganization}
              progress={progress}
            />
          );
        case 6:
          return (
            <Step5Loading
              key="step6-loading"
              onComplete={handleLoadingComplete}
              userInfo={userInfo}
              selectedOrganization={selectedOrganization}
            />
          );
        default:
          return null;
      }
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <AnimatePresence>
        <ModalOverlay
          variants={fadeInOut}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={{ duration: 0.3 }}
          onClick={e => e.stopPropagation()} // Prevent closing on overlay click
        >
          <Modal
            initial={{ opacity: 0, scale: 0.98, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.98, y: 8 }}
            transition={{ type: "spring", stiffness: 220, damping: 26 }}
            onClick={e => e.stopPropagation()}
            width="auto"
            maxWidth="720px"
          >
            <ModalContentWrapper>
              <ModalContent padding="40px">
                <StepContent>
                  <AnimatePresence mode="wait" initial={false} exitBeforeEnter>
                    {renderStep()}
                  </AnimatePresence>
                </StepContent>
              </ModalContent>
            </ModalContentWrapper>
          </Modal>
        </ModalOverlay>
      </AnimatePresence>
    </>
  );
};

export default OnboardingFlow;
